import { useState, useEffect, useCallback } from 'react'
import { useClientAuth } from '../../context/ClientAuthContext'
import { CreditCard, TrendingUp, TrendingDown, Calendar, DollarSign, Clock } from 'lucide-react'
import './ClientPortal.css'

export default function ClientBilling() {
  const { clientApi } = useClientAuth()
  const [billing, setBilling] = useState(null)
  const [transactions, setTransactions] = useState([])
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth())

  useEffect(() => {
    loadBillingData()
  }, [])

  useEffect(() => {
    loadReport()
  }, [selectedMonth])

  function getCurrentMonth() {
    return new Date().toISOString().slice(0, 7) // YYYY-MM
  }

  const loadBillingData = async () => {
    try {
      const [billingData, transactionsData] = await Promise.all([
        clientApi('/billing'),
        clientApi('/billing/history?limit=20')
      ])

      if (billingData.success) setBilling(billingData.billing)
      if (transactionsData.success) setTransactions(transactionsData.transactions)
    } catch (error) {
      console.error('Failed to load billing data:', error)
    }
    setLoading(false)
  }

  const loadReport = async () => {
    try {
      const data = await clientApi(`/billing/report?month=${selectedMonth}`)
      if (data.success) setReport(data)
    } catch (error) {
      console.error('Failed to load report:', error)
    }
  }

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    })
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'credit': return <TrendingUp size={16} className="tx-icon credit" />
      case 'debit': return <TrendingDown size={16} className="tx-icon debit" />
      case 'refund': return <TrendingUp size={16} className="tx-icon refund" />
      default: return <DollarSign size={16} className="tx-icon" />
    }
  }

  // Generate month options for dropdown
  const getMonthOptions = () => {
    const options = []
    const current = new Date()
    for (let i = 0; i < 12; i++) {
      const d = new Date(current.getFullYear(), current.getMonth() - i, 1)
      const value = d.toISOString().slice(0, 7)
      const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      options.push({ value, label })
    }
    return options
  }

  if (loading) {
    return (
      <div className="client-loading">
        <div className="loading-spinner"></div>
        <p>Loading billing...</p>
      </div>
    )
  }

  return (
    <div className="client-billing-page">
      <div className="client-page-header">
        <h1>Billing</h1>
        <p>View your balance and transaction history</p>
      </div>

      {/* Billing Stats */}
      <div className="client-stat-blocks">
        <div className="client-stat-block green">
          <div className="client-stat-icon">
            <DollarSign size={22} />
          </div>
          <div className="client-stat-content">
            <div className="client-stat-value">{formatCurrency(billing?.balance)}</div>
            <div className="client-stat-label">Current Balance</div>
          </div>
        </div>

        <div className="client-stat-block blue">
          <div className="client-stat-icon">
            <CreditCard size={22} />
          </div>
          <div className="client-stat-content">
            <div className="client-stat-value">{formatCurrency(billing?.monthly_cost)}</div>
            <div className="client-stat-label">Monthly Cost</div>
          </div>
        </div>

        <div className="client-stat-block orange">
          <div className="client-stat-icon">
            <TrendingDown size={22} />
          </div>
          <div className="client-stat-content">
            <div className="client-stat-value">{formatCurrency(billing?.daily_cost)}</div>
            <div className="client-stat-label">Daily Cost</div>
          </div>
        </div>

        <div className={`client-stat-block ${billing?.days_remaining <= 7 ? 'red' : 'cyan'}`}>
          <div className="client-stat-icon">
            <Clock size={22} />
          </div>
          <div className="client-stat-content">
            <div className="client-stat-value">
              {billing?.days_remaining || 0}
            </div>
            <div className="client-stat-label">Days Remaining</div>
          </div>
        </div>
      </div>

      {/* Monthly Report */}
      <div className="client-section">
        <div className="client-table-container">
          <div className="client-table-header">
            <h2>Monthly Report</h2>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="month-select"
            >
              {getMonthOptions().map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {report && (
            <div className="report-summary">
              <div className="report-stat">
                <span className="report-label">Days Billed</span>
                <span className="report-value">{report.summary?.days_billed || 0}</span>
              </div>
              <div className="report-stat">
                <span className="report-label">Total Bot-Days</span>
                <span className="report-value">{report.summary?.total_bot_days || 0}</span>
              </div>
              <div className="report-stat">
                <span className="report-label">Avg Daily Bots</span>
                <span className="report-value">{report.summary?.avg_daily_bots || 0}</span>
              </div>
              <div className="report-stat highlight">
                <span className="report-label">Total Charged</span>
                <span className="report-value">{formatCurrency(report.summary?.total_deducted)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Transaction History */}
      <div className="client-section">
        <div className="client-table-container">
          <div className="client-table-header">
            <h2>Recent Transactions</h2>
          </div>

          <table className="client-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    No transactions yet
                  </td>
                </tr>
              ) : (
                transactions.map(tx => (
                  <tr key={tx.id}>
                    <td>{formatDate(tx.created_at)}</td>
                    <td>
                      <span className={`tx-type ${tx.type}`}>
                        {getTransactionIcon(tx.type)}
                        {tx.type}
                      </span>
                    </td>
                    <td>{tx.description || '-'}</td>
                    <td className={`tx-amount ${tx.type}`}>
                      {tx.type === 'credit' || tx.type === 'refund' ? '+' : '-'}
                      {formatCurrency(Math.abs(tx.amount))}
                    </td>
                    <td>{formatCurrency(tx.balance_after)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .month-select {
          padding: 8px 12px;
          background: var(--bg-input);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          color: var(--text-primary);
          font-size: 14px;
          cursor: pointer;
        }
        .month-select:hover {
          border-color: var(--primary);
        }

        .report-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 16px;
          padding: 20px;
        }

        .report-stat {
          padding: 16px;
          background: var(--bg-elevated);
          border-radius: 10px;
          text-align: center;
          border: 1px solid var(--border-color);
        }

        .report-stat.highlight {
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.25);
        }

        .report-label {
          display: block;
          font-size: 12px;
          color: var(--text-muted);
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .report-value {
          display: block;
          font-size: 20px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .tx-type {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          text-transform: capitalize;
        }

        .tx-icon.credit { color: #22c55e; }
        .tx-icon.debit { color: #f87171; }
        .tx-icon.refund { color: #3b82f6; }

        .tx-amount.credit, .tx-amount.refund { color: #22c55e; }
        .tx-amount.debit { color: #f87171; }

        .client-stat-value.warning { color: #f59e0b; }
      `}</style>
    </div>
  )
}
